import clsx from "clsx";
import React, { useId, useState } from "react";

import { createPluggable } from "pluggable-react";
import ReactSelect, {
  type ActionMeta,
  type ClearIndicatorProps,
  components,
  type ControlProps,
  type GroupBase, InputActionMeta,
  type IndicatorsContainerProps,
  type InputProps as ReactSelectInputProps,
  type MenuProps,
  type MultiValueProps,
  type OptionProps,
  type OptionsOrGroups,
  type Props as ReactSelectProps,
  type SelectInstance,
} from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import type { InputProps } from "../../../types/FormTypes";
import "./Select.scss";
import { composeRefs } from "@radix-ui/react-compose-refs";
import { mergePropsWithEventHandlers } from "../../../utils";
import { withRefProp } from "../../../utils/withRefProp";
import Button from "../../common/Button/Button";
import Chip from "../../common/Chip/Chip";
import FloatingPanel from "../../common/FloatingPanel/FloatingPanel";
import Icon from "../../common/Icon/Icon";
import Popover from "../../common/Popover/Popover";
import { usePopover } from "../../common/Popover/PopoverContext";
import Pressable from "../../common/Pressable/Pressable";
import VirtualizedMenuList from "./VirtualizedMenuList";

type SelectContextValue = {
  clearable: boolean;
  hasValue: boolean;
  inputValue: string;
  setInputValue: (value: string) => void;
  clearAll: () => void;
  disableMenuClosing: boolean;
  menuProps?: React.ComponentPropsWithoutRef<"div">;
  portal?: boolean;
  selectInstanceRef: React.RefObject<SelectInstance<SelectOption, false, GroupBase<SelectOption>>>;
};

const SelectContext = React.createContext<SelectContextValue | null>(null);

export interface SelectOption {
  value: string;
  label: string;
  isDisabled?: boolean;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

export type SelectOptions = OptionsOrGroups<SelectOption, SelectGroup>;

/** Async mode: pass this as options. Loading state is derived from the loader promise. */
export interface AsyncOptionsLoader {
  /** Loads options for the given input. */
  loader: (inputValue: string) => Promise<SelectOptions>;
  /** Initial options: true = load on open, or an array of options. */
  defaultOptions?: SelectOptions | boolean;
  /** Cache key: when this changes, the options cache is cleared. */
  cacheKey?: string | number | boolean | null;
}

const isAsyncOptionsLoader = (o: SelectOptions | AsyncOptionsLoader): o is AsyncOptionsLoader =>
  typeof o === "object" && o !== null && "loader" in o && typeof (o as AsyncOptionsLoader).loader === "function";

export interface SelectProps
  extends Omit<
      ReactSelectProps<SelectOption, false, GroupBase<SelectOption>>,
      | "options"
      | "onChange"
      | "value"
      | "onCreateOption"
      | "placeholder"
      | "isDisabled"
      | "className"
      | "filterOption"
      | "isMulti"
      | "components"
      | "onBlur"
      | "onFocus"
      | "styles"
  | "inputId"
  | "isClearable" | "isSearchable"
    >,
    InputProps<string | null> {
  ref?: React.Ref<SelectInstance<SelectOption, false, GroupBase<SelectOption>>>;
  /** Static options or an async loader (async select). */
  options: SelectOptions | AsyncOptionsLoader;
  placeholder?: string;
  searchable?: boolean;
  clearable?: boolean;
  disableMenuClosing?: boolean;
  allowArbitrary?: boolean;
  components?: ReactSelectProps<SelectOption, false, GroupBase<SelectOption>>["components"];
  renderMenuItem?: (props: any) => React.ReactNode;
  menuProps?: React.ComponentPropsWithoutRef<"div">;
  /** Forces the menu into the global popover layer, including when the field sits inside another overlay. */
  portal?: boolean;
  startAdornment?: React.ReactNode;
  /**
   * Shown at the end of the control, before the clear and dropdown indicators, such as an action button.
   * Pointer and key events inside it do not open the menu.
   */
  endAdornment?: React.ReactNode;
}

const findOptionInOptions = (opts: SelectOptions, value: string): SelectOption | null => {
  if (!Array.isArray(opts)) return null;
  for (const opt of opts) {
    if ("options" in opt) {
      const found = findOptionInOptions(opt.options, value);
      if (found) return found;
    } else {
      if (opt.value === value) return opt;
    }
  }
  return null;
};

const SelectControl = (controlProps: ControlProps<SelectOption, false, GroupBase<SelectOption>>) => {
  const popover = usePopover();
  const startAdornment = (controlProps.selectProps as any)?.startAdornment;

  return (
    <components.Control
      {...controlProps}
      innerRef={composeRefs(controlProps.innerRef, popover?.refs.setReference)}
      innerProps={{
        ...mergePropsWithEventHandlers(
          // @ts-ignore
          controlProps.innerProps,
          popover?.getTriggerProps?.() as Record<string, unknown> | undefined,
        ),
        onKeyDown: undefined,
      }}
    >
      {startAdornment ? <span className="deci-select__start-adornment">{startAdornment}</span> : null}
      {controlProps.children}
    </components.Control>
  );
};

const SelectInput = (props: ReactSelectInputProps<SelectOption, false, GroupBase<SelectOption>>) => {
  return (
    <components.Input
    data-select-input={props.selectProps.inputId}
      {...props}
    />
  );
};

const SelectMenu = (props: MenuProps<SelectOption, false, GroupBase<SelectOption>>) => {
  const popover = usePopover();
  const ctx = React.useContext(SelectContext);
  const customMenuProps = ctx?.menuProps;
  const {
    className: customMenuClassName,
    children: _customMenuChildren,
    ...otherCustomMenuProps
  } = customMenuProps || {};
  const disableMenuClosing = Boolean(ctx?.disableMenuClosing);

  return (
    <Popover.Content
      handleVisibility={false}
      portal={ctx?.portal}
      asChild
    >
      <FloatingPanel
        onWheel={(e) => e.stopPropagation()}
        open={popover?.visible}
        placement={popover?.placement}
        ref={props.innerRef}
        {...(props.innerProps)}
        {...otherCustomMenuProps}
        onMouseDown={(e) => {
          if (!disableMenuClosing) {
            props.innerProps?.onMouseDown?.(e as React.MouseEvent<HTMLDivElement>);
          }
        }}
        className={clsx("deci-select__menu", props.className, customMenuClassName)}
      >
        {props.children}
      </FloatingPanel>
    </Popover.Content>
  );
};

const SelectMultiValue = (multiProps: MultiValueProps<SelectOption, true, GroupBase<SelectOption>>) => {
  const { data, removeProps, isDisabled } = multiProps;
  return (
    <Chip
      size="sm"
      className="deci-select__multi-chip"
    >
      <Chip.Label>{data.label}</Chip.Label>
      {!isDisabled ? (
        <Button
          icon
          size="sm"
          className="deci-select__multi-chip-remove"
          label={`Remove ${data.label}`}
          onClick={(event) => {
            event.stopPropagation();
            removeProps.onClick?.(event as any);
          }}
          onMouseDown={(event) => {
            event.stopPropagation();
            removeProps.onMouseDown?.(event as any);
          }}
        >
          <Icon name="close" />
        </Button>
      ) : null}
    </Chip>
  );
};

const SelectClearIndicator = () => {
  const ctx = React.useContext(SelectContext);
  if (!ctx) return null;

  return (
    <Button
      icon
      size="sm"
      style={{ marginRight: "0.25rem" }}
      label="Clear selection"
      onMouseDown={(event) => {
        event.stopPropagation();
        event.preventDefault();
        ctx.clearAll();
      }}
      onTouchEnd={(event) => {
        event.stopPropagation();
        ctx.clearAll();
      }}
    >
      <Icon name="close" />
    </Button>
  );
};

const stopSelectEvent = (event: React.SyntheticEvent) => event.stopPropagation();

const SelectIndicatorsContainer = (props: IndicatorsContainerProps<SelectOption, false, GroupBase<SelectOption>>) => {
  const { children } = props;
  const ctx = React.useContext(SelectContext);
  const canClear = Boolean(ctx?.clearable) && Boolean((ctx?.inputValue?.length ?? 0) > 0 || ctx?.hasValue);
  const endAdornment = (props.selectProps as any)?.endAdornment;

  return (
    <>
      {endAdornment ? (
        // The control opens the menu on mouse down and handles keys; keep those inside the adornment.
        <span
          className="deci-select__end-adornment"
          onMouseDown={stopSelectEvent}
          onTouchEnd={stopSelectEvent}
          onKeyDown={stopSelectEvent}
        >
          {endAdornment}
        </span>
      ) : null}
      {canClear ? <SelectClearIndicator /> : null}
      {children}
    </>
  );
};

const DefaultSelect = withRefProp((props: SelectProps) => {
  const {
    className,
    options: optionsProp,
    value,
    onChange,
    onValueChange,
    placeholder = "Select...",
    disabled = false,
    searchable,
    clearable,
    disableMenuClosing,
    allowArbitrary = false,
    fullWidth = false,
    renderMenuItem,
    menuProps,
    components: customComponents,
    portal,
    error,
    closeMenuOnSelect,
    onInputChange,
    onBlur,
    ref,
    onMenuClose,
    onMenuOpen,
    id,
    inputValue,
    ...otherProps
  } = props;

  const isAsync = isAsyncOptionsLoader(optionsProp);
  const isMulti = Boolean((props as any).isMulti);
  const staticOptions: SelectOptions = isAsync ? [] : (optionsProp as SelectOptions);
  const asyncLoader = isAsync ? (optionsProp as AsyncOptionsLoader) : null;
  const [asyncOptions, setAsyncOptions] = useState<SelectOptions | undefined>(undefined);
  const [menuVisible, setMenuVisible] = useState(false);
  const [internalInputValue, setInternalInputValue] = useState("");
  const selectRef = React.useRef<SelectInstance<SelectOption, false, GroupBase<SelectOption>> | null>(null);
  const menuCloseCountRef = React.useRef(0);

  const loadOptions = React.useMemo(() => {
    if (!asyncLoader) return undefined;
    const { loader } = asyncLoader;
    return (inputValue: string, callback: (options: SelectOptions) => void) => {
      loader(inputValue).then((opts) => {
        setAsyncOptions(opts);
        callback(opts);
      });
    };
  }, [asyncLoader]);

  // Convert string value to SelectOption for react-select
  const selectedOption = React.useMemo(() => {
    if (value === null || value === undefined) {
      return null;
    }
    const foundOption = findOptionInOptions(asyncOptions || staticOptions, value);
    if (foundOption) return foundOption;
    // Async or allowArbitrary: show value as selected even when not in current options
    if (isAsync || allowArbitrary) {
      return { value, label: value };
    }
    return null;
  }, [value, staticOptions, asyncOptions, allowArbitrary, isAsync]);

  const handleChange = React.useCallback(
    (option: SelectOption | null, meta: ActionMeta<any>) => {
      let newValue = Array.isArray(option) ? option : option?.value || null;
      if (meta.action === "remove-value") {
        newValue = (value as any).filter((item: any) => item !== meta.removedValue);
      }

      if (onChange) {
        onChange({ target: { value: newValue } } as any);
      }
      if (onValueChange) {
        onValueChange(newValue as any);
      }
      if (isMulti && meta?.action === "select-option") {
        setMenuVisible(true);
      }
    },
    [isMulti, onChange, onValueChange],
  );

  const handleCreateOption = React.useCallback(
    (inputValue: string) => {
      if (onChange) {
        onChange({ target: { value: inputValue } } as any);
      }
      if (onValueChange) {
        onValueChange(inputValue);
      }
    },
    [onChange, onValueChange],
  );

  const SelectComponent = allowArbitrary ? CreatableSelect : isAsync ? AsyncSelect : ReactSelect;

  const OptionComponent = React.useMemo(() => {
    if (!renderMenuItem) return MenuItem;
    return (optionProps: any) => renderMenuItem(optionProps);
  }, [renderMenuItem]);

  const selectComponents = React.useMemo(
    () => ({
      Control: SelectControl,
      Input: SelectInput,
      IndicatorsContainer: SelectIndicatorsContainer,
      ClearIndicator: () => null,
      Menu: SelectMenu,
      MenuList: VirtualizedMenuList,
      Option: OptionComponent as any,
      MultiValue: SelectMultiValue as any,
      ...(customComponents ?? {}),
    }),
    [OptionComponent, customComponents],
  );

  const handleInputChange = React.useCallback(
    (inputValue: string, actionMeta: InputActionMeta) => {
      setInternalInputValue(inputValue);
      if (
        closeMenuOnSelect === false &&
        !inputValue &&
        (actionMeta.action === "set-value" || actionMeta.action === "input-blur" || actionMeta.action === "menu-close")
      ) {
        return inputValue;
      }
      return onInputChange?.(inputValue, actionMeta) ?? inputValue;
    },
    [onInputChange],
  );

  const autoId = useId();

  const handleMenuClose = () => {
    if (disableMenuClosing) {
      return;
    }
    menuCloseCountRef.current += 1;
    setMenuVisible(false);
    onMenuClose?.();
  };

  const effectiveInputValue = inputValue ?? internalInputValue;
  const hasCurrentValue = Array.isArray(value) ? value.length > 0 : Boolean(value);
  const clearAll = React.useCallback(() => {
    if (selectRef.current?.inputRef) {
      selectRef.current.inputRef.value = "";
    }
    handleInputChange("", { action: "input-change", prevInputValue: effectiveInputValue } as InputActionMeta);
    if (hasCurrentValue) selectRef.current?.clearValue();
  }, [effectiveInputValue, hasCurrentValue, handleInputChange]);

  const selectContextValue = React.useMemo<SelectContextValue>(
    () => ({
      clearable: Boolean(clearable),
      hasValue: hasCurrentValue,
      inputValue: effectiveInputValue || "",
      setInputValue: setInternalInputValue,
      clearAll,
      disableMenuClosing: Boolean(disableMenuClosing),
      menuProps,
      portal,
      selectInstanceRef: selectRef as React.RefObject<SelectInstance<SelectOption, false, GroupBase<SelectOption>>>,
    }),
    [clearable, hasCurrentValue, effectiveInputValue, clearAll, disableMenuClosing, menuProps, portal],
  );

  const commonProps = {
    inputId: id ?? autoId,
    className: clsx(className, fullWidth && "deci-select--full-width", error && "deci-select--has-error"),
    classNamePrefix: "deci-select",
    menuPlacement: "auto" as const,
    value: isMulti ? (value as any) : selectedOption,
    onChange: handleChange,
    placeholder,
    isDisabled: disabled,
    isSearchable: searchable,
    closeMenuOnSelect: closeMenuOnSelect ?? (!isMulti),
    menuIsOpen: menuVisible,
    onMenuOpen: () => {
      onMenuOpen?.();
      setMenuVisible(true)
    },
    onMenuClose: handleMenuClose,
    components: selectComponents,
    controlShouldRenderValue: true,
    isClearable: clearable,
    onInputChange: handleInputChange,
    "data-full-width": fullWidth ? "true" : "false",
  };

  return (
    <SelectContext.Provider value={selectContextValue}>
      <Popover
        fill={true}
        visible={menuVisible || otherProps?.menuIsOpen}
        placement="bottom-start"
        mode="click"
        interactable={true}
      >
        <SelectComponent
          ref={composeRefs(selectRef, ref)}
          {...commonProps}
          options={staticOptions}
          {...(isAsync && asyncLoader
            ? {
              loadOptions,
              defaultOptions: asyncLoader.defaultOptions,
              cacheOptions: asyncLoader.cacheKey,
              filterOption: null, // filtering is done by async loader
            }
            : {})}
          {...(allowArbitrary ? { onCreateOption: (props as any).onCreateOption ?? handleCreateOption } : {})}
          {...otherProps}
          inputValue={effectiveInputValue || ""}
        />
      </Popover>
    </SelectContext.Provider>
  );
});

export interface SelectMenuItemProps extends Omit<Partial<OptionProps>, "type"> {
  ref?: React.Ref<HTMLButtonElement>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const MenuItem = withRefProp((props: SelectMenuItemProps) => {
  const { children, ref, isDisabled, innerRef, innerProps, isSelected, isFocused, onClick } = props;
  return (
    <Pressable
      ref={composeRefs(innerRef as React.Ref<HTMLButtonElement>, ref)}
      {...(innerProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      onClick={(event) => {
        innerProps?.onClick?.(event as any);
        onClick?.(event);
      }}
      disabled={isDisabled}
      aria-disabled={isDisabled ? true : undefined}
      className="deci-select__menu-item"
      data-selected={isSelected ? "" : undefined}
      data-focused={isFocused && !isSelected ? "" : undefined}
    >
      <Pressable.Ripple />
      {children}
    </Pressable>
  );
});

const Select = createPluggable<SelectProps>("deci.select", DefaultSelect);

export default Object.assign(Select, {
  MenuItem,
});
